import * as React from "react";
import { useEffect } from "react";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import Stack from "@mui/material/Stack";
import WifiIcon from "@mui/icons-material/Wifi";
import {
  useGetSmartNodeStatusQuery,
  useEditJobCardMutation,
} from "../api/apiSlice";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import NavbarStepper from "./Stepper/NavBarStepper";
import theme from "../../Global/theme";
import CircleIcon from "@mui/icons-material/Circle";
import { useConnectionMonitor, getConnectionQualityColor, getConnectionQualityText } from "./connectionsTest";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import { showError } from "../../Global/notifications/notificationsSlice";
import stepSaveMessage from "./stepSaveMessage";
import { useDispatch } from "react-redux";
import AbortJobButton from "./AbortJobButton";

const AMBER = "#FDB940";
const GREEN = "#22C55E";
const STATUS_POLL_MS = 15000;

export default function ConnectionTestPage() {
  // Stepper state restoration
  useStepperStateRestoration();

  let { serial_number, imsi_number } = useParams();

  const QueryParams = React.useMemo(() => ({ serial_number }), [serial_number]);
  // The page drew only while the last check had succeeded, so TEST CONNECTION offline (a
  // failed check) left a black screen until a manual refresh (stress test 26/10, P2). It now
  // stays up, says the check couldn't run, and checks again when signal returns.
  //
  // It also re-checks every 15 s until the device reads connected. It read the status once,
  // so a device that came online after the page opened was only seen on a manual retry
  // (NEU-1115). Requiring a recent report as well waits on who writes `connected`.
  const [seenConnected, setSeenConnected] = React.useState(false);
  const {
    data: smart_node_data,
    isError: isCheckFailed,
    error: checkError,
    isFetching,
    refetch: refetchSmartNodeData,
  } = useGetSmartNodeStatusQuery(QueryParams, {
    refetchOnReconnect: true,
    pollingInterval: seenConnected ? 0 : STATUS_POLL_MS,
  });
  const tjommieConnected = !isCheckFailed && !!smart_node_data?.connected;
  React.useEffect(() => {
    if (tjommieConnected) setSeenConnected(true);
  }, [tjommieConnected]);

  // "Testing…" is for the check the installer asked for. The 15 s background poll would
  // otherwise flash it and disable the button four times a minute.
  const [manualCheck, setManualCheck] = React.useState(false);
  React.useEffect(() => {
    if (!isFetching) setManualCheck(false);
  }, [isFetching]);
  const isChecking = isFetching && manualCheck;

  const { isConnected } = useConnectionMonitor();

  const [showTroubleshooting, setShowTroubleshooting] = React.useState(false);
  const timerRef = React.useRef(null);
  const abortRef = React.useRef(null);

  const startTroubleshootTimer = React.useCallback(() => {
    clearTimeout(timerRef.current);
    setShowTroubleshooting(false);
    timerRef.current = setTimeout(() => setShowTroubleshooting(true), 30000);
  }, []);

  const handleRetry = React.useCallback(() => {
    startTroubleshootTimer();
    setManualCheck(true);
    refetchSmartNodeData();
  }, [startTroubleshootTimer, refetchSmartNodeData]);

  React.useEffect(() => {
    startTroubleshootTimer();
    return () => clearTimeout(timerRef.current);
  }, [startTroubleshootTimer]);

  React.useEffect(() => {
    if (smart_node_data?.connected) {
      clearTimeout(timerRef.current);
      setShowTroubleshooting(false);
    }
  }, [smart_node_data?.connected]);

  // Show stepper + spinner while waiting for first response
  if (!smart_node_data && !isCheckFailed) {
    return (
      <>
        <NavbarStepper />
        <Box sx={{ display: "flex", justifyContent: "center", pt: "60px" }}>
          <CircularProgress size={32} sx={{ color: AMBER }} />
        </Box>
      </>
    );
  }

  const drawerOpen = showTroubleshooting && !tjommieConnected && !isCheckFailed;

  return (
    <>
      <NavbarStepper />

      <Box sx={{ px: "16px", pt: "8px" }}>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", mb: "16px" }}>
          Test the device connection.
        </Typography>

        {/* Device card */}
        <Box sx={{
          backgroundColor: "#161B22",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "16px",
          p: "16px",
        }}>
          {/* Header: WiFi icon + status */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "16px" }}>
            <Box sx={{
              width: "48px", height: "48px", borderRadius: "12px",
              backgroundColor: "rgba(253,185,64,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <WifiIcon sx={{ color: AMBER, fontSize: "24px" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1, mb: "4px" }}>
                Device Details
              </Typography>
              <DeviceStatus connected={tjommieConnected} checkFailed={isCheckFailed} />
            </Box>
          </Box>

          {/* Serial + IMSI side by side */}
          <Box sx={{ display: "flex", gap: "8px", mb: "8px" }}>
            <InfoBox label="Serial" value={serial_number} />
            <InfoBox label="IMSI" value={imsi_number} />
          </Box>

          {/* Address */}
          <Box sx={{ mb: "16px" }}>
            <InfoBox label="Address" value={smart_node_data?.address ?? "—"} fullWidth />
          </Box>

          {/* Test Connection button */}
          <Button
            variant="contained"
            disableElevation
            fullWidth
            onClick={handleRetry}
            disabled={isChecking}
            startIcon={isChecking ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            sx={{
              backgroundColor: AMBER,
              color: "#0d1117",
              fontWeight: 700,
              borderRadius: "10px",
              py: "13px",
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
              "&.Mui-disabled": { opacity: 0.55 },
            }}
          >
            {isChecking ? "Testing…" : "Test Connection"}
          </Button>
        </Box>

        {/* Check failed note below the card */}
        {isCheckFailed && (
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.45)", px: "4px", mt: "12px" }}>
            {navigator.onLine
              ? "The test didn't go through. Tap Test Connection to try again."
              : "No signal to reach the server. It tests again when signal is back, or tap Test Connection."}
            {/* The server's reason, when it gave one (NEU-113). */}
            {navigator.onLine && checkError?.data?.error_message && ` (${checkError.data.error_message})`}
          </Typography>
        )}
      </Box>

      <Box sx={{ height: "80px" }} />

      {/* Fixed bottom bar */}
      <Box sx={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        display: "flex", gap: "12px",
        px: "16px", py: "14px",
        background: "rgba(13,14,20,0.95)",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <AbortJobButton ref={abortRef} />
        <NextButton connected={tjommieConnected} isSignalOk={isConnected} />
      </Box>

      {/* Connection timeout bottom sheet */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setShowTroubleshooting(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1A1C24",
            borderRadius: "20px 20px 0 0",
            px: "20px",
            pt: "12px",
            pb: "32px",
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: "rgba(255,255,255,0.2)", mx: "auto", mb: "20px" }} />

        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#ef4444", mb: "10px" }}>
          Connection test timed out
        </Typography>
        <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.6, mb: "16px" }}>
          The device didn't respond within 30 seconds. Work through the checklist below — each step is something technicians have seen fix this in the field.
        </Typography>

        <Box component="ul" sx={{ pl: "20px", m: 0, mb: "24px" }}>
          {/* The checklist ops confirmed on 20/08 (NEU-113, NEU-645); the help panel uses the same steps. */}
          {[
            "Switch the geyser breaker OFF, wait 5 seconds, then switch it ON.",
            "Check Tjommie's LED — it should be blue, flashing intermittently.",
            "Wait up to 2 minutes — the first connection to the network can take that long.",
          ].map((step, i) => (
            <Box component="li" key={i} sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.88rem", lineHeight: 1.6, mb: "6px" }}>
              {step}
            </Box>
          ))}
        </Box>

        <Button
          variant="contained"
          disableElevation
          fullWidth
          onClick={handleRetry}
          sx={{
            backgroundColor: AMBER,
            color: "#0d1117",
            fontWeight: 700,
            borderRadius: "10px",
            py: "13px",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            mb: "10px",
            "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
          }}
        >
          Retry test
        </Button>

        <Button
          fullWidth
          onClick={() => { setShowTroubleshooting(false); abortRef.current?.openDialog(); }}
          sx={{
            py: "13px",
            borderRadius: "10px",
            backgroundColor: "#0d1117",
            color: "#e0e6f0",
            fontSize: "1rem",
            fontWeight: 600,
            textTransform: "none",
            border: "1px solid rgba(255,255,255,0.12)",
            "&:hover": { backgroundColor: "#161b22" },
          }}
        >
          Abort installation
        </Button>
      </Drawer>
    </>
  );
}

function DeviceStatus({ connected, checkFailed }) {
  if (checkFailed) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#9ca3af", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "rgba(255,255,255,0.55)" }}>Couldn't test</Typography>
      </Box>
    );
  }
  if (connected) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: GREEN, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: GREEN }}>Connected</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#ef4444", flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#ef4444" }}>Not connected</Typography>
    </Box>
  );
}

function InfoBox({ label, value, fullWidth }) {
  return (
    <Box sx={{
      flex: fullWidth ? undefined : 1,
      backgroundColor: "rgba(34,197,94,0.06)",
      border: "1px solid rgba(34,197,94,0.18)",
      borderRadius: "10px",
      p: "10px 12px",
      textAlign: "center",
    }}>
      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", mb: "4px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: GREEN, wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  );
}

// Legacy helper — kept for reference, no longer rendered in the main card
function ConnectionStatus({ connected, checkFailed }) {
  if (checkFailed) {
    return (
      <>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>Couldn't test Tjommie</Typography>
          <CircleIcon sx={{ color: "grey" }} />
        </Stack>
        <Typography sx={{ mt: 0.5, fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>
          {navigator.onLine
            ? "The test didn't go through. Tap Test Connection to try again."
            : "No signal to reach the server. It tests again when signal is back, or tap Test Connection."}
        </Typography>
      </>
    );
  }
  if (connected) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography>Tjommie Connected</Typography>
        <CircleIcon sx={{ color: "#119B05" }} />
      </Stack>
    );
  } else if (!connected) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography>Tjommie Not Connected</Typography>
        <CircleIcon sx={{ color: "red" }} />
      </Stack>
    );
  } else {
    return <Typography>Tjommie Status Unknown</Typography>;
  }
}

// Legacy helper — kept for reference, no longer rendered in the main card
function ApiConnectionStatus({ connectionQuality, responseTime, isWeakConnection }) {
  const qualityColor = getConnectionQualityColor(connectionQuality);
  const qualityText = getConnectionQualityText(connectionQuality, responseTime);

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
      <Typography>API {qualityText}</Typography>
      <CircleIcon sx={{ color: qualityColor }} />
      {isWeakConnection && (
        <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
          (Slow Connection)
        </Typography>
      )}
    </Stack>
  );
}

function NextButton({ connected, isSignalOk }) {

  let { serial_number, imsi_number } = useParams();
  let { job_card_reference_id } = useParams();
  let navigate = useNavigate();

  const [editJobCard, { isSuccess: isEditJobCardSuccess, isLoading: isEditJobCardLoading }] =
    useEditJobCardMutation();
  const dispatch = useDispatch();

  const handleConnectionTestCompleted = () => {
    const body = {
      reference_id: job_card_reference_id,
      state: "AFT",
    };
    editJobCard({ body: body }).unwrap().catch((error) => dispatch(showError(stepSaveMessage(error))));
  };

  useEffect(() => {
    if (isEditJobCardSuccess) {
      navigate(`/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/after_install/`);
    }
  }, [isEditJobCardSuccess]);

  return (
    <Button
      variant="contained"
      disableElevation
      sx={{
        flex: 1,
        ...theme.yellowButtonStyle.root,
        border: "none",
        borderRadius: "10px",
        py: "13px",
        fontSize: "1rem",
        textTransform: "none",
        opacity: (!connected || !isSignalOk || isEditJobCardLoading) ? 0.55 : 1,
        "&.Mui-disabled": { opacity: 0.55 },
      }}
      onClick={handleConnectionTestCompleted}
      disabled={!connected || !isSignalOk || isEditJobCardLoading}
      startIcon={isEditJobCardLoading ? <CircularProgress size={20} color="inherit" /> : null}
    >
      {isEditJobCardLoading ? "Processing..." : "Next →"}
    </Button>
  );
}
